import apiClient from '../config/axiosConfig.js';
import { uploadBytes } from './resumableUpload.js';

/**
 * Bulk-import client service. Mirrors AudioSplitService's job + polling shape.
 *
 * Every control-plane call accepts an `options` object passed straight through
 * to axios so the CALLER can opt out of the global error toast
 * ({ skipGlobalErrorToast: true }) and surface ONE summary for the whole batch —
 * otherwise N failed files = N toasts (see FileBrowser.handleDelete).
 */

/**
 * Mint signed upload URLs for a WAVE of files. Call this per concurrency wave,
 * NOT once for the whole batch — a signed upload URL has a bounded lifetime, so a
 * 1000-file batch minted up front would have its late files upload after their
 * URL has already died.
 *
 * @param {Array<{clientKey:string,originalname:string,mimetype:string,size:number}>} files
 * @returns {Promise<Array<{clientKey:string,fileId:string,signedUrl:string,gcsDestinationPath:string,uploadMode:string}>>}
 */
export async function batchInitUpload(files, options = {}) {
    const res = await apiClient.post('/api/import/batch-init', { files }, options);
    return res.data.files;
}

/**
 * Upload one file's bytes DIRECTLY to GCS (the backend is never in the byte
 * path, and the upload carries no auth token, so it must not go through
 * apiClient's interceptors). `minted` is the per-file record from `batchInit`
 * ({ signedUrl, uploadMode, ... }); `uploadBytes` dispatches to a simple
 * single-PUT or a resumable session per `uploadMode`.
 *
 * `onProgress(loaded, total)` reports RAW byte counts (not a percentage) so the
 * caller can aggregate throughput across many concurrent files for speed/ETA.
 * `signal` is an optional AbortSignal so an in-flight upload can be cancelled.
 */
export function uploadToSignedUrl(minted, file, onProgress, signal) {
    return uploadBytes(minted, file, { onProgress, signal });
}

/**
 * Register an uploaded batch as a background import job.
 * @param {{files:Array, createGroup?:boolean, groupName?:string}} payload
 *   files: [{clientKey, fileId, gcsDestinationPath, originalname, mimetype,
 *            encoding?, folderPath?, tags?:string[], metadata?:object, transcribe?}]
 * @returns {Promise<{jobId:string}>}
 */
export async function createImport(payload, options = {}) {
    const res = await apiClient.post('/api/import/create', payload, options);
    return res.data;
}

/** Poll a bulk-import job's progress. Returns the BulkImportJob doc. */
export async function getImportStatus(jobId, options = {}) {
    const res = await apiClient.get('/api/import/status', { params: { jobId }, ...options });
    return res.data.job;
}
